import * as CL from '@design-system/component-library';
import { useState } from 'react';
import { dsClass } from '../../common/constants/dsClasses.js';
import { DeviceListStatusCoCo } from '../../common/enums.js';
import {
  alvZeroMsg,
  billingAccountSelectedPerOrderMsg,
  catalogCorporateMessageLabelMsg,
  companyMsg,
  companysShareOfMonthlyFeeMsg,
  contractPeriodMsg,
  damageInsuranceMsg,
  defaultBillingAccountMsg,
  deviceEnrollmentProgramMsg,
  deviceSelectionMsg,
  inUseMsg,
  notInUseMsg,
  productTypeMsg,
  restrictedBillingAccountsMsg,
  returnMsg,
  t,
} from '../../common/i18n/index.js';
import { getBillingAccountLabelCoCo } from '../../common/utils/billingAccountUtilsCoCo.js';
import {
  ONE_TIME_PAYMENT_CONTRACT_TERM,
  getCompanyShareKey,
  getContractTermLabel,
  getSelectedContractTerms,
  toCompanyShare,
} from '../../common/utils/deviceListFormUtilsCoCo.js';
import { getDeviceSelectionProductTypeLabel } from '../../common/utils/deviceListTabsUtilsCoCo.js';
import { getDamageInsuranceText, getInUseText } from '../../common/utils/deviceListUtilsCoCo.js';
import { toDeviceSelectionProductType } from '../../common/utils/deviceSelectionListUtilsCoCo.js';
import { formatMonthlyPriceCoCo } from '../../common/utils/priceUtilsCoCo.js';
import { EmployeeProductType } from '../../generated/api/employeeProductType.js';
import { BillingAccountInfoCoCo } from '../PlaceOrder/steps/payment/SelectBillingAccountCoCo.js';
import type { DeviceListNewLoaderDataCoCo } from '../../common/loaders.js';
import type {
  DeviceListBillingSettingsCoCo,
  DeviceListFormValuesCoCo,
} from '../../common/utils/deviceListFormUtilsCoCo.js';
import type { ReactNode } from 'react';

import './DeviceListFormCoCo.scss';

const getContractTermText = (values: DeviceListFormValuesCoCo, contractTerm: string) => {
  const contractTermText = getContractTermLabel(contractTerm);
  if (contractTerm === ONE_TIME_PAYMENT_CONTRACT_TERM) {
    return contractTermText;
  }
  const companyShare = toCompanyShare(values.catalog.companyShare?.[getCompanyShareKey(contractTerm)]);
  return values.catalog.productType === EmployeeProductType.EPP && companyShare
    ? `${contractTermText}, ${t.H8Q4(companysShareOfMonthlyFeeMsg)} (${t.S8TX(alvZeroMsg)}) ${formatMonthlyPriceCoCo(companyShare)}`
    : contractTermText;
};

const getBillingItems = (billing: DeviceListBillingSettingsCoCo, data: DeviceListNewLoaderDataCoCo) => {
  const defaultBillingAccount = data.billingAccounts.find(({ id }) => id === billing.billingAccountId);
  const restrictedBillingAccounts = data.billingAccounts.filter(
    ({ id }) => id && billing.selectedBillingAccountIds?.includes(id)
  );
  return [
    {
      title: t.EYPA(defaultBillingAccountMsg),
      // A billing account created in the form is not yet in the loaded billing accounts
      description: defaultBillingAccount ? (
        <>
          <div>{defaultBillingAccount.abpAccountNumber}</div>
          <BillingAccountInfoCoCo billingAccount={defaultBillingAccount} />
        </>
      ) : (
        billing.billingAccountId
      ),
    },
    {
      title: t.RXBO(billingAccountSelectedPerOrderMsg),
      description: getInUseText(billing.approverMustSelectBillingAccount),
    },
    {
      title: t.RP0S(restrictedBillingAccountsMsg),
      description:
        restrictedBillingAccounts.length > 0
          ? restrictedBillingAccounts.map(billingAccount => (
              <div key={billingAccount.id}>{getBillingAccountLabelCoCo(billingAccount)}</div>
            ))
          : t.MUF5(notInUseMsg),
    },
  ];
};

const SummarySection = ({
  heading,
  items,
}: {
  heading?: string;
  items: { title: string; description: ReactNode }[];
}) => (
  <>
    {heading && <h3>{heading}</h3>}
    <CL.Description columns={1} items={items} />
    <hr className={dsClass.MARGIN_VERTICAL_3} />
  </>
);

export interface DeviceListSummaryCoCoProps {
  data: DeviceListNewLoaderDataCoCo;
  values: DeviceListFormValuesCoCo;
  onReturn: () => void;
  onSave: (status: DeviceListStatusCoCo) => Promise<void>;
}

export const DeviceListSummaryCoCo = ({ data, values, onReturn, onSave }: DeviceListSummaryCoCoProps) => {
  const [savingStatus, setSavingStatus] = useState<DeviceListStatusCoCo>();
  const { catalog } = values;
  const isEpp = catalog.productType === EmployeeProductType.EPP;
  const deviceSelection = data.deviceSelections.find(({ id }) => id === catalog.employeeProductSelection);
  const contractTerms = getSelectedContractTerms(catalog.productType, catalog.contractTerms);

  const handleSave = async (status: DeviceListStatusCoCo) => {
    setSavingStatus(status);
    try {
      await onSave(status);
    } finally {
      setSavingStatus(undefined);
    }
  };

  return (
    <div className="of-device-list-summary-coco">
      <SummarySection
        items={[
          { title: t.KJTS(companyMsg), description: data.companyInfo?.companyName },
          { title: t.IVJB('Name of device list'), description: catalog.name },
          {
            title: t.R6HP(productTypeMsg),
            description: getDeviceSelectionProductTypeLabel(toDeviceSelectionProductType(catalog.productType)),
          },
          { title: t.AHB4(deviceSelectionMsg), description: deviceSelection?.name },
          {
            title: t.ULI0(contractPeriodMsg),
            description: contractTerms.map(contractTerm => (
              <div key={contractTerm}>{getContractTermText(values, contractTerm)}</div>
            )),
          },
          ...(isEpp
            ? []
            : [
                {
                  title: t.L7VP('Show purchased device price to employees'),
                  description: getInUseText(catalog.showPurchaseDevicePrice),
                },
              ]),
          ...(isEpp
            ? [
                {
                  title: t.QSXP(damageInsuranceMsg),
                  description: getDamageInsuranceText(catalog.damageInsurance),
                },
              ]
            : []),
          {
            title: t.C8DA(catalogCorporateMessageLabelMsg),
            description: values.isCorporateMessageAdded ? catalog.corporateMessage : t.MUF5(notInUseMsg),
          },
          {
            title: t.Q03S(deviceEnrollmentProgramMsg),
            description: catalog.enrollmentProgramConsent
              ? `${t.V34H(inUseMsg)}, alias: ${catalog.enrollmentProgramAlias || '-'}`
              : t.MUF5(notInUseMsg),
          },
        ]}
      />
      <SummarySection heading={t.IXED('Device catalog billing information')} items={getBillingItems(catalog, data)} />
      {isEpp && catalog.useDifferentPurchasedDeviceBillingAccount && (
        <SummarySection
          heading={t.DEPW('Billing information for purchased devices')}
          items={getBillingItems(catalog.purchasedDeviceBilling ?? {}, data)}
        />
      )}
      <div className={`${dsClass.PADDING_VERTICAL_2} device-list-form-coco__actions`}>
        <CL.Button size="l" color="link" disabled={Boolean(savingStatus)} onClick={onReturn}>
          {t.VH59(returnMsg)}
        </CL.Button>
        <CL.Button
          size="l"
          color="light"
          loading={savingStatus === DeviceListStatusCoCo.DRAFT}
          disabled={Boolean(savingStatus)}
          onClick={() => handleSave(DeviceListStatusCoCo.DRAFT)}
        >
          {t.TJKD('Save draft')}
        </CL.Button>
        <CL.Button
          size="l"
          loading={savingStatus === DeviceListStatusCoCo.PUBLISHED}
          disabled={Boolean(savingStatus)}
          onClick={() => handleSave(DeviceListStatusCoCo.PUBLISHED)}
        >
          {t.JC06('publish')}
        </CL.Button>
      </div>
    </div>
  );
};
